import React, { useEffect, useState } from 'react';
import { CalendarIcon, CheckCircleIcon, ClockIcon, AlertTriangleIcon, XIcon, AlertCircleIcon, TrendingUpIcon, ShieldIcon, SparklesIcon, SettingsIcon, BellIcon, PauseIcon, Trash2Icon } from 'lucide-react';
interface UpcomingPaymentsProps {
  selectedPaymentId?: string | null;
  onPaymentManaged?: () => void;
}
export function UpcomingPayments({
  selectedPaymentId,
  onPaymentManaged
}: UpcomingPaymentsProps) {
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentManageModal, setShowPaymentManageModal] = useState(false);
  const [alertDays, setAlertDays] = useState(3);
  const [pauseMonths, setPauseMonths] = useState(1);
  const [actionComplete, setActionComplete] = useState(false);
  const [actionType, setActionType] = useState<'cancel' | 'pause' | 'alert' | null>(null);
  const currentDate = new Date();
  const currentDay = currentDate.getDate();
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const remainingDays = daysInMonth - currentDay;
  const currentBalance = 4258.0; // From AccountSummary
  const payments = [{
    id: 1,
    name: 'Mortgage',
    amount: 1200.0,
    date: 'May 1',
    logo: 'https://via.placeholder.com/40/754acd/FFFFFF?text=M',
    isPaid: true,
    category: 'Housing'
  }, {
    id: 2,
    name: 'Council Tax',
    amount: 180.0,
    date: 'May 5',
    logo: 'https://via.placeholder.com/40/754acd/FFFFFF?text=CT',
    isPaid: true,
    category: 'Bills'
  }, {
    id: 3,
    name: 'Water Bill',
    amount: 45.0,
    date: 'May 10',
    logo: 'https://via.placeholder.com/40/754acd/FFFFFF?text=W',
    isPaid: true,
    category: 'Utilities'
  }, {
    id: 4,
    name: 'Electricity & Gas',
    amount: 120.0,
    date: 'May 15',
    logo: 'https://via.placeholder.com/40/754acd/FFFFFF?text=EG',
    isPaid: true,
    category: 'Utilities'
  }, {
    id: 5,
    name: 'Internet',
    amount: 35.0,
    date: 'May 18',
    logo: 'https://via.placeholder.com/40/e33ca8/FFFFFF?text=I',
    isPaid: true,
    category: 'Utilities'
  }, {
    id: 6,
    name: 'Netflix',
    amount: 14.99,
    date: 'May 26',
    logo: 'https://logo.clearbit.com/netflix.com',
    isPaid: false,
    category: 'Subscriptions'
  }, {
    id: 7,
    name: 'Spotify',
    amount: 9.99,
    date: 'May 28',
    logo: 'https://logo.clearbit.com/spotify.com',
    isPaid: false,
    category: 'Subscriptions'
  }, {
    id: 8,
    name: 'Amazon Prime',
    amount: 7.99,
    date: 'May 30',
    logo: 'https://logo.clearbit.com/amazon.com',
    isPaid: false,
    category: 'Subscriptions'
  }, {
    id: 9,
    name: 'Mobile Phone',
    amount: 45.0,
    date: 'May 22',
    logo: 'https://via.placeholder.com/40/e33ca8/FFFFFF?text=MP',
    isPaid: false,
    category: 'Bills'
  }, {
    id: 10,
    name: 'Gym Membership',
    amount: 29.99,
    date: 'May 25',
    logo: 'https://via.placeholder.com/40/754acd/FFFFFF?text=GM',
    isPaid: false,
    category: 'Subscriptions'
  }];
  const paidPayments = payments.filter(payment => payment.isPaid);
  const upcomingPayments = payments.filter(payment => !payment.isPaid);
  const totalPaid = paidPayments.reduce((sum, payment) => sum + payment.amount, 0);
  const totalUpcoming = upcomingPayments.reduce((sum, payment) => sum + payment.amount, 0);
  const totalMonthly = totalPaid + totalUpcoming;
  const remainingBalance = currentBalance - totalUpcoming;
  const isEnoughMoney = remainingBalance > 0;
  const criticalPayments = [payments.find(p => p.name === 'Mortgage'), payments.find(p => p.name === 'Council Tax'), payments.find(p => p.name === 'Water Bill'), payments.find(p => p.name === 'Electricity & Gas'), payments.find(p => p.name === 'Internet')].filter(Boolean);
  const disposablePayments = payments.filter(p => !criticalPayments.includes(p));
  const newlyDetectedPayments = [{
    id: 'new1',
    name: 'YouTube Premium',
    amount: 11.99,
    date: 'Detected May 20',
    logo: 'https://logo.clearbit.com/youtube.com',
    category: 'Subscriptions',
    firstDetected: 'May 20, 2024'
  }, {
    id: 'new2',
    name: 'ChatGPT Plus',
    amount: 20.0,
    date: 'Detected May 15',
    logo: 'https://logo.clearbit.com/openai.com',
    category: 'Subscriptions',
    firstDetected: 'May 15, 2024'
  }];
  // Auto-open management modal when selectedPaymentId is provided
  useEffect(() => {
    if (selectedPaymentId) {
      const payment = newlyDetectedPayments.find(p => p.id === selectedPaymentId);
      if (payment) {
        setSelectedPayment(payment);
        setShowManageModal(true);
        setShowPaymentManageModal(true);
      }
    }
  }, [selectedPaymentId]);
  const handleManagePayment = payment => {
    setSelectedPayment(payment);
    setShowPaymentManageModal(true);
    setActionComplete(false);
    setActionType(null);
  };
  const handleCancelPayment = () => {
    setActionType('cancel');
    setActionComplete(true);
    setTimeout(() => {
      setShowPaymentManageModal(false);
      setActionComplete(false);
      if (onPaymentManaged) {
        onPaymentManaged();
      }
    }, 2000);
  };
  const handlePausePayment = () => {
    setActionType('pause');
    setActionComplete(true);
    setTimeout(() => {
      setShowPaymentManageModal(false);
      setActionComplete(false);
      if (onPaymentManaged) {
        onPaymentManaged();
      }
    }, 2000);
  };
  const handleSetAlert = () => {
    setActionType('alert');
    setActionComplete(true);
    setTimeout(() => {
      setShowPaymentManageModal(false);
      setActionComplete(false);
      if (onPaymentManaged) {
        onPaymentManaged();
      }
    }, 2000);
  };
  return <div className="bg-card rounded-xl shadow-sm" data-id="element-792">
      <div className="p-4 border-b border-gray-100" data-id="element-793">
        <div className="flex justify-between items-center" data-id="element-794">
          <h2 className="text-lg font-semibold text-text-primary" data-id="element-795">
            Monthly Payments Tracker
          </h2>
          <button onClick={() => setShowManageModal(true)} className="text-primary text-sm font-medium hover:underline" data-id="element-796">
            Manage
          </button>
        </div>
      </div>
      {/* Management Modal */}
      {showManageModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-797">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-798">
            <div className="sticky top-0 bg-white p-5 border-b border-gray-100 flex justify-between items-start" data-id="element-799">
              <div data-id="element-800">
                <h3 className="text-xl font-bold text-text-primary" data-id="element-801">
                  Manage Regular Payments
                </h3>
                <p className="text-sm text-text-secondary mt-1" data-id="element-802">
                  Review and manage your recurring payments
                </p>
              </div>
              <button onClick={() => setShowManageModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-803">
                <XIcon className="w-5 h-5" data-id="element-804" />
              </button>
            </div>
            <div className="p-5 space-y-6" data-id="element-805">
              {/* Newly Detected Payments */}
              {newlyDetectedPayments.length > 0 && <div data-id="element-806">
                  <div className="flex items-center mb-3" data-id="element-807">
                    <SparklesIcon className="w-5 h-5 text-amber-500 mr-2" data-id="element-808" />
                    <h4 className="text-base font-semibold text-text-primary" data-id="element-809">
                      Newly Detected Payments
                    </h4>
                    <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full" data-id="element-810">
                      {newlyDetectedPayments.length} New
                    </span>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-3" data-id="element-811">
                    <div className="flex items-start" data-id="element-812">
                      <AlertCircleIcon className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-813" />
                      <div data-id="element-814">
                        <p className="text-sm font-medium text-amber-900" data-id="element-815">
                          Auto-detected subscriptions
                        </p>
                        <p className="text-xs text-amber-700 mt-1" data-id="element-816">
                          These recurring payments have been automatically added
                          to your tracker.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-817">
                    {newlyDetectedPayments.map(payment => <div key={payment.id} className="bg-white border border-amber-200 rounded-lg p-4 hover:border-primary/50 transition-colors" data-id="element-818">
                        <div className="flex items-center justify-between" data-id="element-819">
                          <div className="flex items-center flex-1" data-id="element-820">
                            <img src={payment.logo} alt={payment.name} className="w-10 h-10 rounded-full mr-3 object-contain bg-gray-100 p-1" onError={e => {
                      e.currentTarget.src = `https://via.placeholder.com/40/754acd/FFFFFF?text=${payment.name.charAt(0)}`;
                    }} data-id="element-821" />
                            <div className="flex-1" data-id="element-822">
                              <p className="font-medium text-text-primary" data-id="element-823">
                                {payment.name}
                              </p>
                              <p className="text-xs text-text-muted" data-id="element-824">
                                First detected: {payment.firstDetected}
                              </p>
                            </div>
                          </div>
                          <div className="text-right mr-4" data-id="element-825">
                            <p className="font-medium text-text-primary" data-id="element-826">
                              £{payment.amount.toFixed(2)}
                            </p>
                            <p className="text-xs text-text-muted" data-id="element-827">/month</p>
                          </div>
                          <button onClick={() => handleManagePayment(payment)} className="px-3 py-1.5 text-xs font-medium text-primary border border-primary rounded-lg hover:bg-primary/10 flex items-center" data-id="element-828">
                            <SettingsIcon className="w-3 h-3 mr-1" data-id="element-829" />
                            Manage
                          </button>
                        </div>
                      </div>)}
                  </div>
                </div>}
              {/* Critical Payments */}
              <div data-id="element-830">
                <div className="flex items-center mb-3" data-id="element-831">
                  <ShieldIcon className="w-5 h-5 text-red-500 mr-2" data-id="element-832" />
                  <h4 className="text-base font-semibold text-text-primary" data-id="element-833">
                    Critical Payments
                  </h4>
                  <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full" data-id="element-834">
                    Essential
                  </span>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3" data-id="element-835">
                  <p className="text-xs text-red-800" data-id="element-836">
                    These are essential payments that must be prioritized.
                    Missing these could have serious consequences.
                  </p>
                </div>
                <div className="space-y-2" data-id="element-837">
                  {criticalPayments.map(payment => <div key={payment.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-primary/50 transition-colors" data-id="element-838">
                      <div className="flex items-center justify-between" data-id="element-839">
                        <div className="flex items-center flex-1" data-id="element-840">
                          <img src={payment.logo} alt={payment.name} className="w-10 h-10 rounded-full mr-3 object-contain bg-gray-100 p-1" onError={e => {
                      e.currentTarget.src = `https://via.placeholder.com/40/754acd/FFFFFF?text=${payment.name.charAt(0)}`;
                    }} data-id="element-841" />
                          <div className="flex-1" data-id="element-842">
                            <p className="font-medium text-text-primary" data-id="element-843">
                              {payment.name}
                            </p>
                            <div className="flex items-center text-xs text-text-muted" data-id="element-844">
                              <CalendarIcon className="w-3 h-3 mr-1" data-id="element-845" />
                              {payment.date}
                              {payment.isPaid && <span className="ml-2 text-green-600 flex items-center" data-id="element-846">
                                  <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-847" />
                                  Paid
                                </span>}
                            </div>
                          </div>
                        </div>
                        <div className="text-right mr-4" data-id="element-848">
                          <p className="font-medium text-text-primary" data-id="element-849">
                            £{payment.amount.toFixed(2)}
                          </p>
                          <p className="text-xs text-text-muted" data-id="element-850">
                            {payment.category}
                          </p>
                        </div>
                        <button onClick={() => handleManagePayment(payment)} className="px-3 py-1.5 text-xs font-medium text-primary border border-primary rounded-lg hover:bg-primary/10 flex items-center" data-id="element-851">
                          <SettingsIcon className="w-3 h-3 mr-1" data-id="element-852" />
                          Manage
                        </button>
                      </div>
                    </div>)}
                </div>
                <div className="mt-3 flex justify-between items-center text-sm" data-id="element-853">
                  <span className="text-text-secondary" data-id="element-854">Total Critical:</span>
                  <span className="font-semibold text-text-primary" data-id="element-855">
                    £
                    {criticalPayments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}
                    /month
                  </span>
                </div>
              </div>
              {/* Disposable Payments */}
              <div data-id="element-856">
                <div className="flex items-center mb-3" data-id="element-857">
                  <TrendingUpIcon className="w-5 h-5 text-blue-500 mr-2" data-id="element-858" />
                  <h4 className="text-base font-semibold text-text-primary" data-id="element-859">
                    Disposable Payments
                  </h4>
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full" data-id="element-860">
                    Optional
                  </span>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3" data-id="element-861">
                  <p className="text-xs text-blue-800" data-id="element-862">
                    These payments can be paused or cancelled if you need to
                    reduce spending.
                  </p>
                </div>
                <div className="space-y-2" data-id="element-863">
                  {disposablePayments.map(payment => <div key={payment.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-primary/50 transition-colors" data-id="element-864">
                      <div className="flex items-center justify-between" data-id="element-865">
                        <div className="flex items-center flex-1" data-id="element-866">
                          <img src={payment.logo} alt={payment.name} className="w-10 h-10 rounded-full mr-3 object-contain bg-gray-100 p-1" onError={e => {
                      e.currentTarget.src = `https://via.placeholder.com/40/754acd/FFFFFF?text=${payment.name.charAt(0)}`;
                    }} data-id="element-867" />
                          <div className="flex-1" data-id="element-868">
                            <p className="font-medium text-text-primary" data-id="element-869">
                              {payment.name}
                            </p>
                            <div className="flex items-center text-xs text-text-muted" data-id="element-870">
                              <CalendarIcon className="w-3 h-3 mr-1" data-id="element-871" />
                              {payment.date}
                              {payment.isPaid && <span className="ml-2 text-green-600 flex items-center" data-id="element-872">
                                  <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-873" />
                                  Paid
                                </span>}
                            </div>
                          </div>
                        </div>
                        <div className="text-right mr-4" data-id="element-874">
                          <p className="font-medium text-text-primary" data-id="element-875">
                            £{payment.amount.toFixed(2)}
                          </p>
                          <p className="text-xs text-text-muted" data-id="element-876">
                            {payment.category}
                          </p>
                        </div>
                        <button onClick={() => handleManagePayment(payment)} className="px-3 py-1.5 text-xs font-medium text-primary border border-primary rounded-lg hover:bg-primary/10 flex items-center" data-id="element-877">
                          <SettingsIcon className="w-3 h-3 mr-1" data-id="element-878" />
                          Manage
                        </button>
                      </div>
                    </div>)}
                </div>
                <div className="mt-3 flex justify-between items-center text-sm" data-id="element-879">
                  <span className="text-text-secondary" data-id="element-880">Total Disposable:</span>
                  <span className="font-semibold text-text-primary" data-id="element-881">
                    £
                    {disposablePayments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}
                    /month
                  </span>
                </div>
              </div>
              {/* Summary */}
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-4" data-id="element-882">
                <div className="flex justify-between items-center mb-2" data-id="element-883">
                  <span className="text-sm font-medium text-text-primary" data-id="element-884">
                    Total Monthly Payments
                  </span>
                  <span className="text-lg font-bold text-primary" data-id="element-885">
                    £{totalMonthly.toFixed(2)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-xs" data-id="element-886">
                  <div data-id="element-887">
                    <span className="text-text-secondary" data-id="element-888">Critical:</span>
                    <span className="ml-2 font-medium" data-id="element-889">
                      £
                      {criticalPayments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}
                    </span>
                  </div>
                  <div data-id="element-890">
                    <span className="text-text-secondary" data-id="element-891">Disposable:</span>
                    <span className="ml-2 font-medium" data-id="element-892">
                      £
                      {disposablePayments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white p-5 border-t border-gray-100" data-id="element-893">
              <button onClick={() => setShowManageModal(false)} className="w-full py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90" data-id="element-894">
                Done
              </button>
            </div>
          </div>
        </div>}
      {/* Individual Payment Management Modal */}
      {showPaymentManageModal && selectedPayment && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" data-id="element-895">
          <div className="bg-white rounded-xl max-w-md w-full" data-id="element-896">
            {actionComplete ? <div className="p-6 flex flex-col items-center text-center" data-id="element-897">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-898">
                  <CheckCircleIcon className="w-10 h-10 text-green-600" data-id="element-899" />
                </div>
                <h3 className="text-xl font-bold text-text-primary mb-2" data-id="element-900">
                  {actionType === 'cancel' && 'Payment Cancelled'}
                  {actionType === 'pause' && 'Payment Paused'}
                  {actionType === 'alert' && 'Alert Set'}
                </h3>
                <p className="text-sm text-text-secondary" data-id="element-901">
                  {actionType === 'cancel' && `${selectedPayment.name} has been cancelled and will no longer be charged.`}
                  {actionType === 'pause' && `${selectedPayment.name} has been paused for ${pauseMonths} ${pauseMonths === 1 ? 'month' : 'months'}.`}
                  {actionType === 'alert' && `You'll be notified ${alertDays} ${alertDays === 1 ? 'day' : 'days'} before ${selectedPayment.name} is due.`}
                </p>
              </div> : <>
                <div className="p-5 border-b border-gray-100" data-id="element-902">
                  <div className="flex justify-between items-start mb-4" data-id="element-903">
                    <div data-id="element-904">
                      <h3 className="text-xl font-bold text-text-primary" data-id="element-905">
                        Manage Payment
                      </h3>
                      <p className="text-sm text-text-secondary mt-1" data-id="element-906">
                        {selectedPayment.name}
                      </p>
                    </div>
                    <button onClick={() => setShowPaymentManageModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-907">
                      <XIcon className="w-5 h-5" data-id="element-908" />
                    </button>
                  </div>
                  <div className="flex items-center p-3 bg-gray-50 rounded-lg" data-id="element-909">
                    <img src={selectedPayment.logo} alt={selectedPayment.name} className="w-12 h-12 rounded-full mr-3 object-contain bg-white p-1" onError={e => {
                e.currentTarget.src = `https://via.placeholder.com/48/754acd/FFFFFF?text=${selectedPayment.name.charAt(0)}`;
              }} data-id="element-910" />
                    <div className="flex-1" data-id="element-911">
                      <p className="font-semibold text-text-primary" data-id="element-912">
                        {selectedPayment.name}
                      </p>
                      <p className="text-sm text-text-secondary" data-id="element-913">
                        £{selectedPayment.amount.toFixed(2)}/month
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-4" data-id="element-914">
                  {/* Set Alert */}
                  <div className="border border-gray-200 rounded-lg p-4" data-id="element-915">
                    <div className="flex items-start mb-3" data-id="element-916">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0" data-id="element-917">
                        <BellIcon className="w-5 h-5 text-blue-600" data-id="element-918" />
                      </div>
                      <div className="flex-1" data-id="element-919">
                        <h4 className="font-semibold text-text-primary mb-1" data-id="element-920">
                          Set Payment Alert
                        </h4>
                        <p className="text-xs text-text-secondary mb-3" data-id="element-921">
                          Get notified before this payment is due
                        </p>
                        <div data-id="element-922">
                          <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-923">
                            Alert me {alertDays}{' '}
                            {alertDays === 1 ? 'day' : 'days'} before payment
                          </label>
                          <input type="range" min="1" max="14" value={alertDays} onChange={e => setAlertDays(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary" data-id="element-924" />
                          <div className="flex justify-between mt-1 text-xs text-text-secondary" data-id="element-925">
                            <span data-id="element-926">1 day</span>
                            <span data-id="element-927">7 days</span>
                            <span data-id="element-928">14 days</span>
                          </div>
                        </div>
                        <button onClick={handleSetAlert} className="w-full mt-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-id="element-929">
                          Set Alert
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Pause Payment */}
                  <div className="border border-gray-200 rounded-lg p-4" data-id="element-930">
                    <div className="flex items-start mb-3" data-id="element-931">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0" data-id="element-932">
                        <PauseIcon className="w-5 h-5 text-amber-600" data-id="element-933" />
                      </div>
                      <div className="flex-1" data-id="element-934">
                        <h4 className="font-semibold text-text-primary mb-1" data-id="element-935">
                          Pause Payment
                        </h4>
                        <p className="text-xs text-text-secondary mb-3" data-id="element-936">
                          Temporarily stop this payment
                        </p>
                        <div data-id="element-937">
                          <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-938">
                            Pause for {pauseMonths}{' '}
                            {pauseMonths === 1 ? 'month' : 'months'}
                          </label>
                          <input type="range" min="1" max="12" value={pauseMonths} onChange={e => setPauseMonths(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary" data-id="element-939" />
                          <div className="flex justify-between mt-1 text-xs text-text-secondary" data-id="element-940">
                            <span data-id="element-941">1 month</span>
                            <span data-id="element-942">6 months</span>
                            <span data-id="element-943">12 months</span>
                          </div>
                        </div>
                        <button onClick={handlePausePayment} className="w-full mt-3 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700" data-id="element-944">
                          Pause Payment
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Cancel Payment */}
                  <div className="border border-red-200 rounded-lg p-4 bg-red-50" data-id="element-945">
                    <div className="flex items-start" data-id="element-946">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0" data-id="element-947">
                        <Trash2Icon className="w-5 h-5 text-red-600" data-id="element-948" />
                      </div>
                      <div className="flex-1" data-id="element-949">
                        <h4 className="font-semibold text-text-primary mb-1" data-id="element-950">
                          Cancel Payment
                        </h4>
                        <p className="text-xs text-text-secondary mb-3" data-id="element-951">
                          Permanently stop this recurring payment
                        </p>
                        <button onClick={handleCancelPayment} className="w-full py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700" data-id="element-952">
                          Cancel Payment
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>}
          </div>
        </div>}
      <div className="p-4 bg-gray-50 border-b border-gray-100" data-id="element-953">
        <div className="grid grid-cols-3 gap-2 mb-3" data-id="element-954">
          <div data-id="element-955">
            <p className="text-xs text-text-muted" data-id="element-956">Already Paid</p>
            <p className="text-sm font-semibold text-text-primary" data-id="element-957">
              £{totalPaid.toFixed(2)}
            </p>
          </div>
          <div data-id="element-958">
            <p className="text-xs text-text-muted" data-id="element-959">Upcoming</p>
            <p className="text-sm font-semibold text-text-primary" data-id="element-960">
              £{totalUpcoming.toFixed(2)}
            </p>
          </div>
          <div data-id="element-961">
            <p className="text-xs text-text-muted" data-id="element-962">Total</p>
            <p className="text-sm font-semibold text-text-primary" data-id="element-963">
              £{totalMonthly.toFixed(2)}
            </p>
          </div>
        </div>
        <div className="mb-2" data-id="element-964">
          <div className="flex justify-between text-xs mb-1" data-id="element-965">
            <span data-id="element-966">Monthly spending progress</span>
            <span data-id="element-967">{Math.round(totalPaid / totalMonthly * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-968">
            <div className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full" style={{
            width: `${totalPaid / totalMonthly * 100}%`
          }} data-id="element-969"></div>
          </div>
        </div>
        <div className="mt-3 p-3 rounded-lg border flex items-start" data-id="element-970">
          {isEnoughMoney ? <>
              <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-971" />
              <div data-id="element-972">
                <p className="text-sm font-medium text-text-primary" data-id="element-973">
                  You're on track this month
                </p>
                <p className="text-xs text-text-muted" data-id="element-974">
                  After all payments, you'll have £{remainingBalance.toFixed(2)}{' '}
                  left for the next {remainingDays} days
                </p>
              </div>
            </> : <>
              <AlertTriangleIcon className="w-5 h-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-975" />
              <div data-id="element-976">
                <p className="text-sm font-medium text-text-primary" data-id="element-977">
                  Caution: Potential shortfall
                </p>
                <p className="text-xs text-text-muted" data-id="element-978">
                  You'll need an additional £
                  {Math.abs(remainingBalance).toFixed(2)} to cover all upcoming
                  payments
                </p>
              </div>
            </>}
        </div>
      </div>
      {paidPayments.length > 0 && <>
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-979">
            <div className="flex items-center" data-id="element-980">
              <CheckCircleIcon className="w-4 h-4 text-green-500 mr-1" data-id="element-981" />
              <p className="text-sm font-medium text-text-primary" data-id="element-982">
                Already Paid
              </p>
            </div>
          </div>
          <div className="divide-y divide-gray-100" data-id="element-983">
            {paidPayments.map(payment => <div key={payment.id} className="p-4 flex items-center justify-between" data-id="element-984">
                <div className="flex items-center" data-id="element-985">
                  <img src={payment.logo} alt={payment.name} className="w-10 h-10 rounded-full mr-3 object-contain bg-gray-100 p-1" onError={e => {
              e.currentTarget.src = 'https://via.placeholder.com/40/754acd/FFFFFF?text=' + payment.name.charAt(0);
            }} data-id="element-986" />
                  <div data-id="element-987">
                    <p className="font-medium text-text-primary" data-id="element-988">
                      {payment.name}
                    </p>
                    <p className="flex items-center text-xs text-text-muted" data-id="element-989">
                      <CalendarIcon className="w-3 h-3 mr-1" data-id="element-990" />
                      {payment.date} •{' '}
                      <span className="text-green-600 ml-1" data-id="element-991">Paid</span>
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-992">
                  <p className="font-medium text-text-primary" data-id="element-993">
                    £{payment.amount.toFixed(2)}
                  </p>
                  <p className="text-xs text-text-muted" data-id="element-994">{payment.category}</p>
                </div>
              </div>)}
          </div>
        </>}
      {upcomingPayments.length > 0 && <>
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-995">
            <div className="flex items-center" data-id="element-996">
              <ClockIcon className="w-4 h-4 text-amber-500 mr-1" data-id="element-997" />
              <p className="text-sm font-medium text-text-primary" data-id="element-998">
                Upcoming Payments
              </p>
            </div>
          </div>
          <div className="divide-y divide-gray-100" data-id="element-999">
            {upcomingPayments.map(payment => <div key={payment.id} className="p-4 flex items-center justify-between" data-id="element-1000">
                <div className="flex items-center" data-id="element-1001">
                  <img src={payment.logo} alt={payment.name} className="w-10 h-10 rounded-full mr-3 object-contain bg-gray-100 p-1" onError={e => {
              e.currentTarget.src = 'https://via.placeholder.com/40/754acd/FFFFFF?text=' + payment.name.charAt(0);
            }} data-id="element-1002" />
                  <div data-id="element-1003">
                    <p className="font-medium text-text-primary" data-id="element-1004">
                      {payment.name}
                    </p>
                    <p className="flex items-center text-xs text-text-muted" data-id="element-1005">
                      <CalendarIcon className="w-3 h-3 mr-1" data-id="element-1006" />
                      {payment.date}
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-1007">
                  <p className="font-medium text-text-primary" data-id="element-1008">
                    £{payment.amount.toFixed(2)}
                  </p>
                  <button className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full mt-1" data-id="element-1009">
                    Pay Now
                  </button>
                </div>
              </div>)}
          </div>
        </>}
    </div>;
}